import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AzureCommunicationTokenCredential } from '@azure/communication-common';
import { CallClient, IncomingCall, TeamsCallAgent } from '@azure/communication-calling';
import { BehaviorSubject, Observable, firstValueFrom, from, of } from 'rxjs';
import { catchError, delay, filter, finalize, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CallEvent } from '@nimbus-assistant/enums/call-event';
import { Activity } from '@nimbus-assistant/enums/activity';
import { AppRoutes } from '@nimbus-assistant/enums/app-routes';
import { TaskConversationState, TaskState, ITask } from '@nimbus-assistant/models/task.model';
import { IncomingCallInfo } from '@nimbus-assistant/models/incoming-call-info';
import { CallDirection, CallerInfo, Channel, Modality } from '@nimbus-assistant/models/task-types.model';
import { AuthService } from './auth.service';
import { LoggingService } from './logging.service';
import { TriggerService } from './trigger.service';
import { GraphApiService } from './graph-api.service';
import { PresenceService } from './presence.service';
import { AssistantTasksService } from './assistant-tasks.service';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class TaskDirectCallService {
  private static readonly ACCEPTED_SUBCODE = 10003;
  private static readonly TIMEOUT_SUBCODE = 10004;
  private static readonly IGNORED_SUBCODE = 10024;
  private static readonly CALLER_CANCELED_SUBCODE = 0;
  private static readonly REMOVED_BY_ANOTHER_PARTICIPANT_SUBCODE = 5000;
  private static readonly CALL_END_ACTIVITY_DELAY_MS = 2000;

  private callAgent: TeamsCallAgent | null = null;
  private readonly boundOnIncomingCall = this.onIncomingCall.bind(this);

  private readonly isListening$ = new BehaviorSubject<boolean>(false);

  public constructor(
    private readonly authService: AuthService,
    private readonly tasksService: AssistantTasksService,
    private readonly presenceService: PresenceService,
    private readonly triggerService: TriggerService,
    private readonly graphApiService: GraphApiService,
    private readonly loggingService: LoggingService,
    private readonly router: Router,
  ) {
    this.authService.authenticated$.pipe(untilDestroyed(this)).subscribe((authenticated) => {
      if (authenticated) {
        this.initCallAgent();
      } else {
        this.destroyCallAgent().pipe(untilDestroyed(this)).subscribe();
      }
    });
  }

  // --- Agent lifecycle ---

  private initCallAgent(): void {
    this.destroyCallAgent()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        const client = new CallClient();
        const tokenCredential = new AzureCommunicationTokenCredential({
          tokenRefresher: (): Promise<string> => firstValueFrom(this.authService.acquireTeamsToken()),
        });

        from(client.createTeamsCallAgent(tokenCredential))
          .pipe(
            catchError((err) => {
              this.loggingService.Error(`TaskDirectCallService: agent init failed: ${JSON.stringify(err)}`);
              return of(null);
            }),
            take(1),
            filter((agent) => !!agent),
            untilDestroyed(this),
          )
          .subscribe((agent) => {
            this.callAgent = agent;
            this.callAgent.on('incomingCall', this.boundOnIncomingCall);
            this.isListening$.next(true);
            this.loggingService.Info('TaskDirectCallService: agent listening');
          });
      });
  }

  private destroyCallAgent(): Observable<void> {
    if (!this.callAgent) {
      return of(null);
    }
    this.callAgent.off('incomingCall', this.boundOnIncomingCall);
    this.isListening$.next(false);

    return from(this.callAgent.dispose()).pipe(
      catchError((err) => {
        this.loggingService.Error(`TaskDirectCallService: agent destroy failed: ${JSON.stringify(err)}`);
        return of(null);
      }),
      finalize(() => {
        this.callAgent = null;
      }),
    );
  }

  // --- Incoming call handling ---

  private onIncomingCall(args: { incomingCall: IncomingCall }): void {
    const incomingCall = args?.incomingCall;
    this.loggingService.Info(`TaskDirectCallService: incomingCall ${incomingCall?.id}`);

    const callInfo = new IncomingCallInfo(incomingCall, this.loggingService);

    // Service calls are managed by AssistantTasksService via SignalR — only watch end reason
    if (callInfo.isServiceCall) {
      this.loggingService.Info('TaskDirectCallService: service call — delegating to SignalR flow');
      this.watchCallEndReason(incomingCall, callInfo);
      return;
    }

    // Guard: already in a direct call (ringing or connected), connected service call, or ACW
    if (this.tasksService.directCallTaskSnapshot || this.tasksService.connectedTaskSnapshot || this.tasksService.acwTaskSnapshot) {
      this.loggingService.Warning(`TaskDirectCallService: direct call ${incomingCall.id} ignored — already in call or ACW`);
      return;
    }

    const task = this.buildDirectCallTask(incomingCall.id, callInfo);
    this.tasksService.addDirectCallTask(task);

    // Template origin filtering reads callInfo.isInternal — resolve it first, or internal calls look external.
    this.resolveCallerInternal(callInfo)
      .pipe(take(1), untilDestroyed(this))
      .subscribe((isInternal) => {
        callInfo.callerInternal = isInternal;
        this.tasksService.updateDirectCallTask(incomingCall.id, { isInternal, isExternal: !isInternal });

        // Call may have ended while the check was in flight
        if (this.tasksService.directCallTaskSnapshot?.unifiedConversationId !== incomingCall.id) {
          this.loggingService.Info(`TaskDirectCallService: ringing trigger skipped — direct call ${incomingCall.id} no longer active`);
          return;
        }

        // Fire incoming trigger (skip for supervision calls)
        if (!callInfo.isSupervision) {
          this.triggerService.processCallEvent(CallEvent.IncomingCall, callInfo);
        }
      });

    this.watchCallEndReason(incomingCall, callInfo);
  }

  /** Is the caller an internal (tenant) user? No Teams user id (e.g. direct PSTN) resolves as external. */
  private resolveCallerInternal(callInfo: IncomingCallInfo): Observable<boolean> {
    if (!callInfo.microsoftTeamsUserId) {
      return of(false);
    }

    return this.graphApiService.getUserIdentities(callInfo.microsoftTeamsUserId).pipe(
      map((user) => !!user),
      catchError((err) => {
        this.loggingService.Error(`TaskDirectCallService: caller identity check failed: ${JSON.stringify(err)}`);
        return of(false);
      }),
    );
  }

  // --- Call end/accept/cancel ---

  private watchCallEndReason(incomingCall: IncomingCall, callInfo: IncomingCallInfo): void {
    incomingCall.on('callEnded', (args) => {
      const subCode = args?.callEndReason?.subCode;
      this.loggingService.Info(`TaskDirectCallService: callEnded ${incomingCall.id}, subCode: ${subCode}`);

      if (subCode === TaskDirectCallService.ACCEPTED_SUBCODE) {
        this.handleCallAccepted(incomingCall, callInfo);
      } else if (
        subCode === TaskDirectCallService.TIMEOUT_SUBCODE ||
        subCode === TaskDirectCallService.IGNORED_SUBCODE ||
        subCode === TaskDirectCallService.CALLER_CANCELED_SUBCODE ||
        subCode === TaskDirectCallService.REMOVED_BY_ANOTHER_PARTICIPANT_SUBCODE
      ) {
        if (!callInfo.isServiceCall) {
          this.tasksService.removeDirectCallTask(incomingCall.id);
          this.triggerService.processCallEvent(CallEvent.CallCancelled, callInfo);
        }
      }
    });
  }

  private handleCallAccepted(incomingCall: IncomingCall, callInfo: IncomingCallInfo): void {
    if (callInfo.isServiceCall) {
      // Service call state is managed by SignalR — no action needed here
      return;
    }

    const acceptedByUserRawId = IncomingCallInfo.acceptedByUserRawId(incomingCall);
    const currentUserId = this.authService.profile$.value?.id;
    const isAcceptedByAnotherUser = !!acceptedByUserRawId && !!currentUserId && !acceptedByUserRawId.includes(currentUserId);

    if (isAcceptedByAnotherUser) {
      this.loggingService.Info(`TaskDirectCallService: direct call accepted by another user: ${acceptedByUserRawId}`);
      this.tasksService.removeDirectCallTask(incomingCall.id);
      return;
    }

    // Accepted by current user — transition task to Connected
    this.loggingService.Info(`TaskDirectCallService: direct call accepted by current user: ${incomingCall.id}`);
    this.tasksService.updateDirectCallTask(incomingCall.id, {
      state: TaskState.Connected,
      acceptedAt: new Date().toISOString(),
    });

    if (!callInfo.isSupervision) {
      this.triggerService.processCallEvent(CallEvent.CallAccepted, callInfo);
    }

    this.router.navigate([AppRoutes.call]);
    this.watchDirectCallEnd(incomingCall.id);
  }

  /**
   * Detect direct call end via presence activity change (same mechanism as the old CallService.watchCallEndActivity).
   * The 2-second delay matches the old implementation to allow the in-call presence to arrive first.
   * takeUntil(taskRemoved$) ensures the subscription is cleaned up if the task is removed by another path
   * (e.g. callEnded ACS event) before presence activity changes.
   */
  private watchDirectCallEnd(acsCallId: string): void {
    const taskRemoved$ = this.tasksService.tasks$.pipe(
      filter((tasks) => !tasks.find((t) => t.unifiedConversationId === acsCallId)),
      take(1),
    );

    this.presenceService.activity$
      .pipe(
        // Wait until presence confirms the user is in a call before watching for end
        filter((activity) => this.isInCallActivity(activity)),
        take(1),
        switchMap(() => this.presenceService.activity$),
        filter(() => !!this.tasksService.connectedTaskSnapshot),
        switchMap((activity) => of(activity).pipe(delay(TaskDirectCallService.CALL_END_ACTIVITY_DELAY_MS))),
        filter((activity) => !this.isInCallActivity(activity)),
        filter(() => !!this.tasksService.connectedTaskSnapshot),
        take(1),
        takeUntil(taskRemoved$),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.loggingService.Info(`TaskDirectCallService: direct call end detected via activity for ${acsCallId}`);
        this.tasksService.removeDirectCallTask(acsCallId);
        this.router.navigate([AppRoutes.home]);
      });
  }

  private isInCallActivity(activity: Activity): boolean {
    return activity === Activity.InACall || activity === Activity.InAConferenceCall;
  }

  // --- Task builder ---

  private buildDirectCallTask(acsCallId: string, callInfo: IncomingCallInfo): ITask {
    const caller: CallerInfo = {
      displayName: callInfo.callerDisplayName ?? null,
      office365Id: callInfo.microsoftTeamsUserId ?? null,
      phoneNumber: callInfo.phoneNumber ?? null,
    };

    return {
      unifiedConversationId: acsCallId,
      conversationId: acsCallId,
      userSessionId: null,
      state: TaskState.Ringing,
      conversationState: TaskConversationState.Ongoing,
      since: new Date().toISOString(),
      acceptedAt: null,
      modality: Modality.Audio,
      channel: Channel.AcsCalling,
      direction: CallDirection.Inbound,
      caller,
      teamId: null,
      initialServiceName: null,
      teamInfo: null,
      originType: null,
      isInternal: false, // updated async by GraphAPI check
      isExternal: true,
      isServiceCall: false,
      isDirectCall: true,
      isSupervision: false,
      startedWithTransfer: false,
      orderNumber: 0,
    };
  }
}
